import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { generateUserUrlSearchParams } from '@gbs-monorepo-packages/auth';
import {
  ClientRoles,
  EmployeeRoles,
  HighUserRoles,
} from '@gbs-monorepo-packages/common';
import {
  FORGOT_PASSWORD,
  HOME,
  LOGIN,
  PERMISSION_REDIRECT,
  REDIRECT_LOGIN,
  RESET_PASSWORD,
} from '@gbs-monorepo/edge/constants/RoutePaths';

import { ADMIN_URL, CLIENT_URL, EMPLOYEE_URL } from '../../constants/Env';
import { redirectDashboard } from '../../services/dashboard';
import { getRouteFrom } from '../../utils/getRoutes';

interface IGlobalRedirectProps {
  children?: ReactNode;
  defaultPath?: string;
}

const ALLOWED_ROUTES = [
  getRouteFrom(LOGIN),
  getRouteFrom(FORGOT_PASSWORD),
  getRouteFrom(RESET_PASSWORD),
  getRouteFrom(REDIRECT_LOGIN),
  getRouteFrom(PERMISSION_REDIRECT),
  getRouteFrom(HOME),
];

export const GlobalRedirect = ({
  children,
  defaultPath = '/',
}: IGlobalRedirectProps): JSX.Element => {
  const location = useLocation();
  const token = localStorage.getItem('JWT_TOKEN') ?? '';

  const isAllowedRoute = ALLOWED_ROUTES.some((route) =>
    location.pathname.startsWith(route)
  );

  useEffect(() => {
    const handleExternalRedirect = async () => {
      if (!token) {
        return;
      }

      if (isAllowedRoute) {
        return;
      }

      try {
        const response = await redirectDashboard();
        const userData = response.data;
        const userRoles = userData.roles ?? [];

        const isHighUserRole = HighUserRoles.some((role) =>
          userRoles.includes(role)
        );
        const isClientRole = ClientRoles.some((role) =>
          userRoles.includes(role)
        );
        const isEmployeeRole = EmployeeRoles.some((role) =>
          userRoles.includes(role)
        );

        let domain = '';

        if (isHighUserRole) {
          domain = ADMIN_URL;
        } else if (isClientRole) {
          domain = CLIENT_URL;
        } else if (isEmployeeRole) {
          domain = EMPLOYEE_URL;
        }

        if (domain && domain !== window.location.origin) {
          const userInfoUrlSearchParams = generateUserUrlSearchParams(
            userData,
            token
          );
          userInfoUrlSearchParams.set(
            'originalRoute',
            `${location.pathname}${location.search}`
          );
          const path = `/permission-redirect?${userInfoUrlSearchParams.toString()}`;
          window.location.replace(`${domain}${path}`);
          return;
        }
      } catch (error) {
        window.location.replace(`${ADMIN_URL}`);
      }
    };
    void handleExternalRedirect();
  }, [token, location.pathname, location.search, isAllowedRoute]);

  if (children) {
    return <>{children}</>;
  }

  if (!token && !isAllowedRoute) {
    const fullPath = getRouteFrom(LOGIN);
    return (
      <Navigate to={fullPath} replace state={{ from: location.pathname }} />
    );
  }

  return <Navigate to={defaultPath} replace />;
};

interface IGlobalRedirectRouteProps {
  children?: ReactNode;
  defaultPath?: string;
}

export const GlobalRedirectRoute = ({
  children,
  defaultPath = '/',
}: IGlobalRedirectRouteProps): JSX.Element => {
  return <GlobalRedirect defaultPath={defaultPath}>{children}</GlobalRedirect>;
};
